// Google map for a single DN — driver-focused view.
//
// The map only tracks the delivery staff; the receiver branch is not pinned (its lat/lng is often
// unknown). The marker is the latest known GPS snapshot (delivered > current > dispatch). Without one
// the empty state explains that the driver app has not sent a location yet.

import { Box, Text } from "@mantine/core"
import { MapTrifoldIcon, TruckIcon } from "@phosphor-icons/react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Dot } from "../delivery/atoms"
import { HtmlMarker, MapCanvas, MapPopup } from "../ui/google-map"
import mapClasses from "../ui/google-map/google-map.module.css"
import type { DriverStage } from "./dn-detail-utils"
import classes from "./dn-detail.module.css"

interface Props {
  driverLat?: number | null
  driverLng?: number | null
  driverStage?: DriverStage | null
  driverName?: string | null
  /** Pre-formatted «N мин өмнө». */
  lastSeen?: string | null
  /** Pre-formatted ETA "HH:mm". */
  eta?: string | null
  isLive?: boolean
  height?: number
}

export function DnReceiverMap({ driverLat, driverLng, driverStage, driverName, lastSeen, eta, isLive, height = 320 }: Props) {
  const { t } = useTranslation("delivery")
  const hasDriver = driverLat != null && driverLng != null
  const stage: DriverStage = driverStage ?? "CURRENT"

  const [popupOpen, setPopupOpen] = useState(false)
  const point = useMemo(
    () => (hasDriver ? { lat: driverLat as number, lng: driverLng as number } : null),
    [hasDriver, driverLat, driverLng],
  )

  if (!point) {
    return (
      <Box className={classes.mapEmpty} mih={height}>
        <Box className={classes.vehicleIcon}>
          <MapTrifoldIcon size={22} />
        </Box>
        <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
          {t("dnDetail.mapEmpty")}
          <br />
          {t("dnDetail.mapEmptyHint")}
        </Text>
      </Box>
    )
  }

  const stageLabel = t(`dnDetail.mapStage.${stage}`)

  return (
    <Box className={classes.mapFrame}>
      <MapCanvas height={height} center={point} zoom={15} fitPoints={[point]} scrollWheel={false}>
        <HtmlMarker position={point} onClick={() => setPopupOpen((o) => !o)}>
          <Box className={mapClasses.puck} data-tone={stage === "DELIVERED" ? "green" : "gray"}>
            <TruckIcon size={16} weight="fill" />
          </Box>
        </HtmlMarker>
        {popupOpen && (
          <MapPopup position={point} onClose={() => setPopupOpen(false)}>
            <Text fz="var(--nw-fz-body)" fw={700}>
              {t("dnDetail.mapDriver")}
              {driverName ? ` · ${driverName}` : ""}
            </Text>
            <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
              {stageLabel}
            </Text>
            {lastSeen && (
              <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                {t("dnDetail.mapLastSeen", { time: lastSeen })}
              </Text>
            )}
          </MapPopup>
        )}
      </MapCanvas>

      {isLive && (
        <Box className={classes.mapBadge} top={10} right={10}>
          <Text fz="var(--nw-fz-body)" fw={600} component="span">
            <Dot tone={stage === "DELIVERED" ? "green" : "blue"} /> {stageLabel}
            {lastSeen ? ` · ${lastSeen}` : ""}
          </Text>
        </Box>
      )}

      {eta && stage !== "DELIVERED" && (
        <Box className={classes.mapBadge} bottom={10} left={10}>
          <Text fz="var(--nw-fz-body)" fw={600}>
            {t("dnDetail.eta", { time: eta })}
          </Text>
        </Box>
      )}
    </Box>
  )
}
